import Dexie from 'dexie';
import type { Table } from 'dexie';

import type {
  AppEventSchema,
  CategorySchema,
  ChallengeSchema,
  ConfigSchema,
  GamingSchema,
  ImportSchema,
  LibrarySchema,
  LibraryBookSchema,
  MediaSchema,
  PostSchema,
  ProfileSchema,
  ProgressionSchema,
  QuestionSchema,
  QuizInfoSchema,
  QuizSchema,
  TrophySchema,
  UserSchema,
  AssessmentSchema,
  FavoriteSchema,
  CacheSchema,
  TaskSchema,
  ExerciseSchema,
  ExerciseItemSchema,
  VersionSchema,
  ExerciseInfoSchema,
  CoachingStatSchema,
  RecommendationSchema,
  RecommendationParameterSchema,
  FriendSchema,
  MyFutureBoxSchema,
  SponsorInfoSchema,
  EventSchema,
} from '@/types/schema';

export class NomadDB extends Dexie {
  appEvents!: Table<AppEventSchema>;
  categories!: Table<CategorySchema>;
  challenges!: Table<ChallengeSchema>;
  configs!: Table<ConfigSchema>;
  gaming!: Table<GamingSchema>;
  imports!: Table<ImportSchema>;
  library!: Table<LibrarySchema>;
  libraryBooks!: Table<LibraryBookSchema>;
  media!: Table<MediaSchema>;
  posts!: Table<PostSchema>;
  profiles!: Table<ProfileSchema>;
  progressions!: Table<ProgressionSchema>;
  questions!: Table<QuestionSchema>;
  quizInfos!: Table<QuizInfoSchema>;
  quizzes!: Table<QuizSchema>;
  trophies!: Table<TrophySchema>;
  users!: Table<UserSchema>;
  assessments!: Table<AssessmentSchema>;
  favorites!: Table<FavoriteSchema>;
  cache!: Table<CacheSchema>;
  tasks!: Table<TaskSchema>;
  exercises!: Table<ExerciseSchema>;
  exerciseItems!: Table<ExerciseItemSchema>;
  versions!: Table<VersionSchema>;
  exerciseInfos!: Table<ExerciseInfoSchema>;
  coachingStats!: Table<CoachingStatSchema>;
  recommendations!: Table<RecommendationSchema>;
  recommendationParameters!: Table<RecommendationParameterSchema>;
  friends!: Table<FriendSchema>;
  myFutureBoxes!: Table<MyFutureBoxSchema>;
  sponsorInfos!: Table<SponsorInfoSchema>;
  events!: Table<EventSchema>;

  constructor() {
    super('nomad', { autoOpen: false });
    this.version(2).stores({
      appEvents: '++id',
      categories:
        '&[id+libraryBookId], [contentType+libraryBookId], [slug+libraryBookId], id, slug, contentType, libraryBookId',
      challenges: '&id, slug',
      configs: 'id',
      imports:
        '&[libraryBookId+collection], libraryBookId, collection, updatedAt',
      library: '&id, type, &slug, title',
      libraryBooks: '&id, &slug, metaTitle',
      media: '&[id+libraryBookId], id, libraryBookId',
      posts:
        '&[id+libraryBookId], [slug+libraryBookId], id, slug, libraryBookId',
      profiles: 'id',
      progressions: '&contentId, type, local',
      questions: '&[id+libraryBookId], id, libraryBookId',
      quizzes:
        '&[id+libraryBookId], [slug+libraryBookId], id, slug, libraryBookId',
      trophies: '&id',
      users: 'id',
    });

    this.version(3).stores({
      categories:
        '[id+libraryBookId], id, [contentType+libraryBookId], [slug+libraryBookId], slug, contentType, libraryBookId',
    });

    this.version(4)
      .stores({
        progressions: null,
        progressionsTemp: '&[contentId+member], contentId, type, local',
      })
      .upgrade(async (tx) => {
        const progressions = await tx.table('progressions').toArray();
        return await tx.table('progressionsTemp').bulkAdd(progressions);
      });

    this.version(5)
      .stores({
        progressions: '&[contentId+member], contentId, type, local',
        progressionsTemp: null,
      })
      .upgrade(async (tx) => {
        const progressionsTemp = await tx.table('progressionsTemp').toArray();
        return await tx.table('progressions').bulkAdd(progressionsTemp);
      });

    this.version(6).stores({
      gaming: '&id',
    });

    this.version(7)
      .stores({
        categories: null,
        categoriesTemp:
          '&[id+libraryBookId], [contentType+libraryBookId], [slug+libraryBookId], [title+libraryBookId], title, id, slug, contentType, libraryBookId',
      })
      .upgrade(async (tx) => {
        const categoriesTemp = await tx.table('categories').toArray();
        return await tx.table('categoriesTemp').bulkAdd(categoriesTemp);
      });

    this.version(8)
      .stores({
        categoriesTemp: null,
        categories:
          '&[id+libraryBookId], [contentType+libraryBookId], [slug+libraryBookId], [title+libraryBookId], title, id, slug, contentType, libraryBookId',
      })
      .upgrade(async (tx) => {
        const categories = await tx.table('categoriesTemp').toArray();
        return await tx.table('categories').bulkAdd(categories);
      });

    this.version(9).stores({
      quizInfos:
        '&[id+libraryBookId+memberId], [libraryBookId+memberId], [libraryBookId+memberId+isFinished], [libraryBookId+disciplineId+memberId]',
    });

    this.version(10).stores({
      assessments: '&uuid, libraryBookId, member, id, [libraryBookId+member]',
      favorites: '&id, contextId, [member+contextId]',
    });

    this.version(11)
      .stores({
        quizInfos: null,
        quizInfosTemp:
          '&[id+libraryBookId+memberId], [libraryBookId+memberId], [libraryBookId+memberId+isFinished], [libraryBookId+disciplineId+memberId], [libraryBookId+chapterId+memberId]',
      })
      .upgrade(async (tx) => {
        const quizInfosTemp = await tx.table('quizInfos').toArray();
        return await tx.table('quizInfosTemp').bulkAdd(quizInfosTemp);
      });

    this.version(12)
      .stores({
        quizInfosTemp: null,
        quizInfos:
          '&[id+libraryBookId+memberId], [libraryBookId+memberId], [libraryBookId+memberId+isFinished], [libraryBookId+disciplineId+memberId], [libraryBookId+chapterId+memberId]',
      })
      .upgrade(async (tx) => {
        const quizInfosTemp = await tx.table('quizInfosTemp').toArray();
        return await tx.table('quizInfos').bulkAdd(quizInfosTemp);
      });

    this.version(13).stores({
      cache: '&key',
    });

    this.version(14)
      .stores({
        progressions: null,
        progressionsTemp: '&[type+contentId+member], contentId, type, local',
      })
      .upgrade(async (tx) => {
        const progressions = await tx.table('progressions').toArray();
        return await tx.table('progressionsTemp').bulkAdd(progressions);
      });

    this.version(15)
      .stores({
        progressions: '&[type+contentId+member], contentId, type, local',
        progressionsTemp: null,
      })
      .upgrade(async (tx) => {
        const progressionsTemp = await tx.table('progressionsTemp').toArray();
        return await tx.table('progressions').bulkAdd(progressionsTemp);
      });

    this.version(16).stores({
      exercises:
        '&[id+libraryBookId], [slug+libraryBookId], id, slug, libraryBookId',
      exerciseItems: '&[id+libraryBookId], id, itemType, libraryBookId',
      exerciseInfos:
        '&[id+libraryBookId+memberId], [libraryBookId+memberId], [libraryBookId+memberId+isFinished], [libraryBookId+disciplineId+memberId], [libraryBookId+chapterId+memberId]',
      versions: 'id',
      quizzes: null,
      quizInfos: null,
      questions: null,
    });

    this.version(17)
      .stores({
        categories: null,
        categoriesTemp:
          '&[id+libraryBookId], [contentType+libraryBookId], [slug+libraryBookId], [title+libraryBookId], [disciplineId+chapterId+parentId+contentType], title, id, slug, contentType, libraryBookId',
        media: null,
        mediaTemp: '&[id+libraryBookId], id, libraryBookId, source',
      })
      .upgrade(async (tx) => {
        const categories = await tx.table('categories').toArray();
        await tx.table('categoriesTemp').bulkAdd(categories);

        const media = await tx.table('media').toArray();
        await tx.table('mediaTemp').bulkAdd(media);
      });

    this.version(18)
      .stores({
        categories:
          '&[id+libraryBookId], [contentType+libraryBookId], [slug+libraryBookId], [title+libraryBookId], [disciplineId+chapterId+parentId+contentType], title, id, slug, contentType, libraryBookId',
        categoriesTemp: null,
        media: '&[id+libraryBookId], id, libraryBookId, source',
        mediaTemp: null,
      })
      .upgrade(async (tx) => {
        const categoriesTemp = await tx.table('categoriesTemp').toArray();
        await tx.table('categories').bulkAdd(categoriesTemp);

        const mediaTemp = await tx.table('mediaTemp').toArray();
        await tx.table('media').bulkAdd(mediaTemp);
      });

    this.version(19)
      .stores({
        media: null,
        mediaTemp:
          '&[id+libraryBookId], [libraryBookId+source], id, libraryBookId',

        categories: null,
        categoriesTemp:
          '&[id+libraryBookId], [contentType+libraryBookId], [slug+libraryBookId], [title+libraryBookId], [disciplineId+chapterId+parentId+libraryBookId+contentType], title, id, slug, contentType, libraryBookId',
      })
      .upgrade(async (tx) => {
        const media = await tx.table('media').toArray();
        await tx.table('mediaTemp').bulkAdd(media);

        const categories = await tx.table('categories').toArray();
        await tx.table('categoriesTemp').bulkAdd(categories);
      });

    this.version(20)
      .stores({
        media: '&[id+libraryBookId], [libraryBookId+source], id, libraryBookId',
        mediaTemp: null,

        categories:
          '&[id+libraryBookId], [contentType+libraryBookId], [slug+libraryBookId], [title+libraryBookId], [disciplineId+chapterId+parentId+libraryBookId+contentType], title, id, slug, contentType, libraryBookId',
        categoriesTemp: null,
      })
      .upgrade(async (tx) => {
        const mediaTemp = await tx.table('mediaTemp').toArray();
        await tx.table('media').bulkAdd(mediaTemp);

        const categoriesTemp = await tx.table('categoriesTemp').toArray();
        await tx.table('categories').bulkAdd(categoriesTemp);
      });

    this.version(21).stores({
      coachingStats:
        '&[appId+historyDateUTC+member+total], [appId+member], [appId+total], total, id, historyDateUTC, local',
    });

    this.version(22).stores({
      tasks: '&id, type, state',
    });

    this.version(23).stores({
      recommendations: '&[type+id], id, type, local',
      recommendationParameters: '&[type+id], id, type, local',
    });

    this.version(24).stores({
      friends: '&id, source, [libraryBookId+source]',
    });

    this.version(24)
      .stores({
        friends: '&id',
        media: null,
        mediaTemp:
          '&[id+libraryBookId], [libraryBookId+source], id, libraryBookId, source',
      })
      .upgrade(async (tx) => {
        const media = await tx.table('media').toArray();
        await tx.table('mediaTemp').bulkAdd(media);
      });

    this.version(25)
      .stores({
        media:
          '&[id+libraryBookId], [libraryBookId+source], id, libraryBookId, source',
        mediaTemp: null,
      })
      .upgrade(async (tx) => {
        const mediaTemp = await tx.table('mediaTemp').toArray();
        await tx.table('media').bulkAdd(mediaTemp);
      });

    this.version(26).stores({
      myFutureBoxes: '&id, &slug, displayType',
      events: '&id',
      sponsorInfos: '&id, slug',
    });

    this.version(27)
      .stores({
        eventsTemp: '&id',
        events: null,
      })
      .upgrade(async (tx) => {
        const events = await tx.table('events').toArray();
        await tx.table('eventsTemp').bulkAdd(events);
      });

    this.version(28)
      .stores({
        events: '&id, slug',
        eventsTemp: null,
      })
      .upgrade(async (tx) => {
        const eventsTemp = await tx.table('eventsTemp').toArray();
        await tx.table('events').bulkAdd(eventsTemp);
      });
  }
}
